import useTranslation from 'next-translate/useTranslation'
import { FC, ReactElement } from 'react'
import { Whisper, Popover } from 'rsuite'

import { getCountryAndLocaleStrings } from '../utils/locales'
import { ProductQuantity } from '../utils/types/Product'
import { getStockLimitStatus, StockLimitStatus } from '../lib/product'
import { formatTimeInterval } from '../lib/time'
import useIsOnMobile from '../services/useIsOnMobile'

import styles from '../styles/ProductQuantitySection.module.less'

interface VariantTooltipProps {
  shipmentDays: number
  variantItem: ProductQuantity
  children: ReactElement
  productStockLimit: string | undefined
  salesUnits?: string
  showOutOfStockContent?: boolean
  showInternalStockLimitContent?: boolean
  showOverManualStockContent?: boolean
}

const VariantTooltip: FC<VariantTooltipProps> = (props: VariantTooltipProps) => {
  const {
    children,
    shipmentDays,
    variantItem,
    salesUnits,
    productStockLimit,
    showInternalStockLimitContent,
  } = props

  const { t, lang: countryAndLang } = useTranslation('products')
  const isOnMobile = useIsOnMobile()
  const { locale: lang } = getCountryAndLocaleStrings(countryAndLang)
  const limitStatus = getStockLimitStatus(variantItem)

  const getWarningContent = () => {
    switch (limitStatus) {
      case StockLimitStatus.EXCEEDS_EXACT_STOCK:
        return (
          <p>
            {t('You cannot choose a higher quantity than current stock available Please check back after restock')}
          </p>
        )

      case StockLimitStatus.EXCEEDS_MANUAL_LIMIT:
        return (
          <p>
            {t('Only a few pieces left order now to avoid delays Quantity and delivery time will be confirmed with the supplier')}
            .
          </p>
        )

      case StockLimitStatus.HAS_STOCK_BACK_DATE:
        return (
          <p>
            {`${t('This item is currently being restocked The estimated delivery date after restock is')} ${formatTimeInterval(shipmentDays.toString(), variantItem.stockDate!, undefined, lang)}`}
          </p>
        )

      default:
        return null
    }
  }

  const warningContent = getWarningContent()
  const internalStockContent = showInternalStockLimitContent && salesUnits && (
    <b>
      {variantItem.variantId && variantItem.manualStockLimit ? t('Variant limit') : t('Product limit')}
      :
      {' '}
      {`${variantItem.manualStockLimit ? variantItem.manualStockLimit : productStockLimit} ${salesUnits}`}
    </b>
  )

  // Don't render tooltip if there's no content at all
  if (!warningContent && !internalStockContent) {
    return children
  }

  return (
    <Whisper
      trigger={isOnMobile ? 'none' : 'hover'}
      controlId="hover-control"
      placement="top"
      speaker={(
        <Popover className={styles['whisper-modal']}>
          <div className={styles['variant-tooltip']}>
            {warningContent || internalStockContent}
          </div>
        </Popover>
      )}
    >
      {children}
    </Whisper>
  )
}

export default VariantTooltip
